import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "./context/AuthContext.jsx";

// ---------------------- CODE SPLITTING POR RUTA ----------------------
const Dashboard = lazy(() => import("./pages/dashboardPage/dashboardPage.jsx"));
const UserListPage = lazy(() => import("./pages/userListPage/userListPage.jsx"));
const MetricasPage = lazy(() => import("./pages/metricasPage/metricasPage.jsx"));
const CrmPage = lazy(() => import("./pages/crmPage/crmPage.jsx"));
const ContactPage = lazy(() => import("./pages/contactPage/ContactPage.jsx"));
const BusinessPage = lazy(() => import("./pages/businessPage/BusinessPage.jsx"));
const OmnichannelInbox = lazy(() => import("./pages/chatPage/OmnichannelInbox.jsx"));
const RegistroClienteForm = lazy(
  () => import("./components/forms/clientesPotenciales/layoutFormPublic.jsx"),
);
const ProspectosTracker = lazy(() => import("./pages/tracker/ProspectosTracker.jsx"));
const ThankYouPage = lazy(() => import("./components/forms/paginaDeGracias.jsx"));
const Listas = lazy(() => import("./pages/listas/ListasUser.jsx"));
const ListaDetalles = lazy(() => import("./pages/listas/components/ListaDetalles.jsx"));
const EnviarCorreo = lazy(() => import("./components/correos/enviados/EnviarCorreo.jsx"));
const EnviarCorreoAdmin = lazy(
  () => import("./components/correos/enviados/EnviarCorreoAdmin.jsx"),
);
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.jsx"));
const MisAsignacionesContainer = lazy(
  () => import("./pages/equipo/MisAsignacionesContainer.jsx"),
);

const AsignacionesActualesPage = lazy(
  () => import("./pages/admin/pages/AsignacionesActuales.jsx"),
);
const PublicForm = lazy(() => import("./components/forms/FormBuilder/public/PublicForm.jsx"));
const FormGenerator = lazy(() =>
  import("./components/forms/FormBuilder/manager/FormManager.jsx").then((module) => ({
    default: module.FormGenerator,
  })),
);

const Login = lazy(() => import("./components/login/login.jsx"));

// Rutas adicionales del routesConfig.jsx
const TeamPage = lazy(() => import("./pages/team/TeamPage.jsx"));
const ExternalChatPage = lazy(() => import("./pages/chatPage/ExternalChatPage.jsx"));
const AutomationsPage = lazy(() => import("./pages/automations/AutomationsPage.jsx"));
const AdsPage = lazy(() => import("./pages/ads/AdsPage.jsx"));
const AttributionPage = lazy(() => import("./pages/ads/AttributionPage.jsx"));
const AuditLogPage = lazy(() => import("./pages/audit/AuditLogPage.jsx"));
const ProductsPage = lazy(() => import("./pages/products/ProductsPage.jsx"));
const QuotationsPage = lazy(() => import("./pages/quotations/QuotationsPage.jsx"));
const EmailCampaignsPage = lazy(() => import("./pages/email/EmailCampaignsPage.jsx"));
const EmailSequencesPage = lazy(() => import("./pages/email/EmailSequencesPage.jsx"));
const WhatsAppPage = lazy(() => import("./pages/whatsapp/WhatsAppPage.jsx"));
const ContractsPage = lazy(() => import("./pages/contracts/ContractsPage.jsx"));
const CheckoutPage = lazy(() => import("./pages/checkout/CheckoutPage.jsx"));
const ApiCredentialsPage = lazy(() => import("./pages/integrations/ApiCredentialsPage.jsx"));
const SchedulesPage = lazy(() => import("./pages/schedules/SchedulesPage.jsx"));
const AttendancePage = lazy(() => import("./pages/attendance/AttendancePage.jsx"));

const RouteLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      fontFamily: "Montserrat, sans-serif",
      fontWeight: 700,
      color: "#5E35B1",
      background: "#f8f8ff",
    }}
  >
    Cargando módulo...
  </div>
);

// ---------------------- COMPONENTE NOTIFICADOR (PERSISTENCIA) ----------------------
/**
 * Este componente detecta cada cambio de ruta interna en el CRM
 * y le avisa al Dashboard principal (padre) para que guarde la URL.
 */
// ---------------------- COMPONENTE NOTIFICADOR ACTUALIZADO ----------------------
const NavigationNotifier = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useAuth();

  // 1. Auto-navegación inicial basada en la URL del padre
  //    SOLO despues de que auth haya terminado de cargar
  useEffect(() => {
    if (isLoading) {
      return;
    }
    
    const searchParams = new URLSearchParams(window.location.search);
    const view = searchParams.get('view');
    if (view && location.pathname === '/') {
      navigate(`/${view}`, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, location.pathname]);

  // 2. Sincronización con el padre
  useEffect(() => {
    if (window.parent === window) return;

    // Determinamos el nombre de la vista (quitando el primer /)
    const viewName = location.pathname.substring(1) || null;

    window.parent.postMessage({
      type: "UPDATE_PARENT_URL",
      params: { view: viewName }
    }, '*');
  }, [location]);

  return null;
};

// ---------------------- PROTECCIONES ----------------------
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return null;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  return children;
};

const ProtectedRouteWithRole = ({ children, requiredRoles }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return null;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  return children;
};

// ---------------------- COMPONENTE DE RUTAS ----------------------
const AppRoutes = () => {
  const { user } = useAuth();
  const userId = user?.id || null;

  return (
    <>
      {/* Inyectamos el notificador para que funcione en todas las rutas */}
      <NavigationNotifier />

      <Suspense fallback={<RouteLoader />}>
        <Routes>
        {/* --- INICIO DEL SISTEMA: DASHBOARD --- */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />

        {/* --- PUBLICO / SIN LOGIN --- */}
        <Route path="/login" element={<Login />} />
        <Route path="/registros/:formName" element={<RegistroClienteForm />} />
        <Route
          path="/gracias-por-confiar-en-hitpoly"
          element={<ThankYouPage />}
        />
        <Route path="/registro-cliente" element={<PublicForm />} />

        {/* --------------------- ADMIN --------------------- */}
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRouteWithRole requiredRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRouteWithRole>
          }
        />
        <Route
          path="/asignaciones-actuales"
          element={
            <ProtectedRouteWithRole requiredRoles={["admin"]}>
              <AsignacionesActualesPage />
            </ProtectedRouteWithRole>
          }
        />
        {/* --------------------- EQUIPO --------------------- */}
        <Route
          path="/equipo"
          element={
            <ProtectedRouteWithRole
              requiredRoles={["admin", "empresa", "closer", "setter"]}
            >
              <MisAsignacionesContainer />
            </ProtectedRouteWithRole>
          }
        />


        {/* --------------------- CONTACTO --------------------- */}
        <Route
          path="/pagina-de-contacto/:prospectId"
          element={
            <ProtectedRoute>
              <ContactPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- LISTAS --------------------- */}
        <Route
          path="/todas-las-listas"
          element={
            <ProtectedRoute>
              <Listas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/listas/:nombreLista"
          element={
            <ProtectedRoute>
              <ListaDetalles />
            </ProtectedRoute>
          }
        />

        {/* --------------------- CONTACTOS --------------------- */}
        <Route
          path="/contactos"
          element={
            <ProtectedRoute>
              <UserListPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- OMNICANALIDAD --------------------- */}
        <Route
          path="/mensajeria"
          element={
            <ProtectedRoute>
              <OmnichannelInbox />
            </ProtectedRoute>
          }
        />

        {/* --------------------- CORREOS --------------------- */}
        <Route
          path="/enviar-correo-empresa"
          element={
            <ProtectedRoute>
              <EnviarCorreoAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enviar-correo"
          element={
            <ProtectedRoute>
              <EnviarCorreo />
            </ProtectedRoute>
          }
        />

        {/* --------------------- MÉTRICAS --------------------- */}
        <Route
          path="/metricas"
          element={
            <ProtectedRoute>
              <MetricasPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- CRM --------------------- */}
        <Route
          path="/negocios"
          element={
            <ProtectedRoute>
              <CrmPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/negocio/:businessId"
          element={
            <ProtectedRoute>
              <BusinessPage />
            </ProtectedRoute>
          }
        />
        <Route path="/Negocios" element={<Navigate to="/negocios" replace />} />

        {/* --------------------- TRACKER --------------------- */}
        <Route
          path="/tracker"
          element={
            <ProtectedRoute>
              <ProspectosTracker />
            </ProtectedRoute>
          }
        />

        {/* --------------------- DEBUG --------------------- */}
        <Route
          path="/prueba"
          element={
            <ProtectedRoute>
              <EnviarCorreo />
            </ProtectedRoute>
          }
        />
        <Route path="/prueva" element={<Navigate to="/prueba" replace />} />

        {/* --------------------- GESTOR DE FORMULARIOS --------------------- */}
        <Route
          path="/admin/formularios"
          element={
            <ProtectedRoute>
              <FormGenerator userId={userId} />
            </ProtectedRoute>
          }
        />


        {/* --------------------- EQUIPO --------------------- */}
        <Route
          path="/equipo-gestion"
          element={
            <ProtectedRoute>
              <TeamPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- BANDEJA OMNICANAL --------------------- */}
        <Route
          path="/bandeja-omnicanal"
          element={
            <ProtectedRoute>
              <OmnichannelInbox />
            </ProtectedRoute>
          }
        />

        {/* --------------------- CHAT EXTERNO --------------------- */}
        <Route
          path="/chat-externo"
          element={
            <ProtectedRoute>
              <ExternalChatPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- AUTOMATIZACIONES --------------------- */}
        <Route
          path="/automatizaciones"
          element={
            <ProtectedRoute>
              <AutomationsPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- ADS --------------------- */}
        <Route
          path="/ads"
          element={
            <ProtectedRoute>
              <AdsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads-atribucion"
          element={
            <ProtectedRoute>
              <AttributionPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- AUDITORIA --------------------- */}
        <Route
          path="/auditoria"
          element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- PRODUCTOS --------------------- */}
        <Route
          path="/productos"
          element={
            <ProtectedRoute>
              <ProductsPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- COTIZACIONES --------------------- */}
        <Route
          path="/cotizaciones"
          element={
            <ProtectedRoute>
              <QuotationsPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- EMAIL MARKETING --------------------- */}
        <Route
          path="/email-marketing"
          element={
            <ProtectedRoute>
              <EmailCampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/secuencias-email"
          element={
            <ProtectedRoute>
              <EmailSequencesPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- WHATSAPP --------------------- */}
        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute>
              <WhatsAppPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- CONTRATOS --------------------- */}
        <Route
          path="/contratos"
          element={
            <ProtectedRoute>
              <ContractsPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- CHECKOUT --------------------- */}
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- INTEGRACIONES / CREDENCIALES --------------------- */}
        <Route
          path="/credenciales-api"
          element={
            <ProtectedRoute>
              <ApiCredentialsPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- HORARIOS --------------------- */}
        <Route
          path="/horarios"
          element={
            <ProtectedRoute>
              <SchedulesPage />
            </ProtectedRoute>
          }
        />

        {/* --------------------- ASISTENCIA --------------------- */}
        <Route
          path="/asistencia"
          element={
            <ProtectedRoute>
              <AttendancePage />
            </ProtectedRoute>
          }
        />
        </Routes>
      </Suspense>
    </>
  );
};

// ---------------------- APP (ROOT) ----------------------
function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
